import React, { useState, useEffect, useContext } from "react";
import {
  Typography,
  Box,
  Backdrop,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import axios from "axios";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { PhaseItem } from "../../components/phase/PhaseItem";
import { Add } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import { ThemeContext } from "../../context/ThemeContext";
import { getColors } from "../../themes/theme";
import { AddPhaseDialog } from "../../components/phase/AddPhaseDialog";
import { useSnackbar } from "notistack";

const PhasePage = () => {
  const { token } = useAuthContext();
  const [loading, setLoading] = useState(true); // Loading state for fetching data
  const [phases, setPhases] = useState([]); // State to store exercises fetched from backend
  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const [filteredPhases, setFilteredPhases] = useState([]); // State for filtered exercises
  const { darkMode } = useContext(ThemeContext);
  const [openAddDialog, setOpenAddDialog] = useState(false); // New state for confirmation dialog
  const { enqueueSnackbar } = useSnackbar();

  const updatePhase = (updatedPhase) => {
    setPhases((prevPhases) => {
      let updatedPhases;

      if (updatedPhase.deleted) {
        // Remove the deleted exercise from the state
        updatedPhases = prevPhases.filter(
          (phase) => phase.id !== updatedPhase.id
        );
      } else if (!prevPhases.some((phase) => phase.id === updatedPhase.id)) {
        // Add the new exercise if it doesn't already exist
        updatedPhases = [...prevPhases, updatedPhase];
      } else {
        // Update the existing exercise
        updatedPhases = prevPhases.map((phase) =>
          phase.id === updatedPhase.id ? updatedPhase : phase
        );
      }

      // Sort exercises alphabetically by name
      return updatedPhases.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
      );
    });
  };

  useEffect(() => {
    // Fetch exercises from the backend
    if (!token) return;
    const fetchPhases = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/phase/practiceZero`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setPhases(response.data);
        setFilteredPhases(response.data); // Initialize filtered exercises
      } catch (err) {
        console.error("Error fetching phases:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPhases();
  }, [token]);

  useEffect(() => {
    // Filter exercises based on the search query
    setFilteredPhases(
      phases.filter((phase) =>
        phase.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, phases]);

  const handleAddPhase = async (data) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/phase`,
        {
          ...data,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      updatePhase(response.data); // Update the parent state
      enqueueSnackbar("Successfully Added!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to add phase!", { variant: "error" });
    }
  };

  const handleAddClicked = () => {
    setOpenAddDialog(true);
  };

  const handleCancelAdd = () => {
    setOpenAddDialog(false);
  };

  return (
    <>
      <Box
        sx={{
          pt: "60px",
          pl: 5,
          pr: 10,
        }}
      >
        <Backdrop sx={{ zIndex: 1400 }} open={loading}>
          <CircularProgress size={80} />
        </Backdrop>
        {!loading && (
          <>
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontSize: "28px",
                color: darkMode
                  ? getColors.drawerTextDark
                  : getColors.thumbnailTextLight,
                fontWeight: 600,
                textAlign: "left",
                mb: 5,
              }}
            >
              Global Phase Management
            </Typography>
            <Box display="flex" justifyContent="space-between" sx={{ mb: 3 }}>
              <TextField
                variant="filled"
                placeholder="Search phases"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 0 }}>
                      <SearchIcon sx={{ mt: -2, ml: 1 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                startIcon={<Add />}
                variant="contained"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "18px",
                  fontWeight: 500,
                  px: 3,
                  py: 2,
                  background: darkMode ? "#2D2E30" : "#E6E6E6",
                  color: darkMode
                    ? getColors.primaryTextDark
                    : getColors.secondaryTextLight,
                  "&:hover": {
                    color: getColors.primaryTextDark,
                  },
                }}
                onClick={handleAddClicked}
              >
                Add Phase
              </Button>
            </Box>
            <Box
              display="grid"
              gridTemplateColumns="repeat(3, 1fr)"
              gap={4}
              rowGap={4}
            >
              {filteredPhases.map((phase, index) => (
                <PhaseItem key={index} {...phase} onPhaseUpdate={updatePhase} />
              ))}
            </Box>
          </>
        )}
      </Box>
      <AddPhaseDialog
        open={openAddDialog}
        onClose={handleCancelAdd}
        onAdd={handleAddPhase}
      />
    </>
  );
};

export default PhasePage;
